<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isLoadingSaveBatteryOption"
    :is-loading-data="isGetSubscriptionInProgress || isLoadingBatteryChoices"
  >
    <div data-cy="solar-production-storage-page">
      <header class="text-center pb-8">
        <AtomText
          :text="t('subscription.solarProductionStorage.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="solar-production-storage-page-title"
        />

        <AtomText
          class="mt-2"
          color-variant="description"
          :content-html="t('subscription.solarProductionStorage.description')"
          data-cy="solar-production-storage-page-description"
        />

        <MoleculeTag
          :value="t('subscription.solarProductionStorage.tags.withoutEngagement')"
          type="secondary"
          size="lg"
          class="mt-2"
        />
      </header>
      <OrganismAlert
        v-if="isPreloadedBatteryOfferAlertDisplayed"
        variant="complex"
        state="info"
        has-icon
        custom-icon-name="SiteConsumptionIcon"
        :title="t('subscription.solarProductionStorage.preloadedBatteryOfferAlert.title')"
        :content-text="
          t('subscription.solarProductionStorage.preloadedBatteryOfferAlert.description')
        "
        :action="{
          actionType: 'externalLink',
          label: t('subscription.solarProductionStorage.preloadedBatteryOfferAlert.link'),
          link: t('common.link.preloadedBatteryOffer'),
        }"
        class="mb-8"
        data-cy="solar-production-storage-preloaded-battery-offer-alert"
      />
      <form>
        <div
          class="flex flex-col gap-8 pb-8 md:grid md:grid-cols-2 md:[grid-template-rows:repeat(6,auto)] md:gap-x-8 md:gap-y-6"
        >
          <SbsSeletableCard
            class="flex-1 md:flex-none md:grid md:grid-rows-subgrid md:row-span-6"
            :has-error="isSubmitted && !selectedBattery"
            v-model="selectedBattery"
            :value="EBatteryOptionSelectable.MSB"
            :class="{ 'md:col-span-2 md:w-1/2 md:mx-auto': !isMbCardDisplayed }"
            v-if="isMsbCardDisplayed"
          >
            <template #tag
              ><MoleculeTag
                :value="t('subscription.solarProductionStorage.tags.popularOption')"
                type="offers"
                size="lg"
                variant="icon"
                iconName="StarIcon"
            /></template>
            <template #default="{ isActive }">
              <div class="mb-6 md:mb-0">
                <AtomText
                  :text="
                    t('subscription.solarProductionStorage.batteryStorage.mySmartBattery.name')
                  "
                  color-variant="description"
                  font-size="text-sm"
                />
                <AtomText
                  :text="
                    t('subscription.solarProductionStorage.batteryStorage.mySmartBattery.subtitle')
                  "
                  color-variant="text"
                  font-size="text-lg"
                  fontStyle="font-semibold"
                />
                <AtomText
                  :text="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.mySmartBattery.description'
                    )
                  "
                  color-variant="text"
                  font-size="text-sm"
                />
              </div>
              <div class="mb-6 md:mb-0">
                <div class="mb-2 flex justify-between items-center">
                  <div>
                    <AtomText
                      :text="t('subscription.solarProductionStorage.batteryStorage.common.storage')"
                      color-variant="text"
                      font-size="text-sm"
                      font-style="font-medium"
                    />
                  </div>
                  <div class="text-right">
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.mySmartBattery.storageText'
                        )
                      "
                      color-variant="text"
                      font-size="text-sm"
                    />
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.mySmartBattery.storageDescription'
                        )
                      "
                      color-variant="description"
                      font-size="text-xs"
                    />
                  </div>
                </div>
                <div class="mb-3 flex justify-between items-center">
                  <div>
                    <AtomText
                      :text="
                        t('subscription.solarProductionStorage.batteryStorage.common.restitution')
                      "
                      color-variant="text"
                      font-size="text-sm"
                      font-style="font-medium"
                    />
                  </div>
                  <div class="text-right">
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.mySmartBattery.restitutionText'
                        )
                      "
                      color-variant="text"
                      font-size="text-sm"
                    />
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.mySmartBattery.restitutionDescription'
                        )
                      "
                      color-variant="description"
                      font-size="text-xs"
                    />
                  </div>
                </div>
                <OrganismAlert
                  hasIcon
                  customIconName="HeartIcon"
                  :content-text="
                    t('subscription.solarProductionStorage.batteryStorage.mySmartBattery.alertText')
                  "
                  state="success"
                />
              </div>
              <AtomDivider class="mb-6 md:mb-0" />
              <MoleculeFrameCard
                spacing="sm"
                content-theme="dark"
                class="mt-3 mb-6 md:mt-0 md:mb-0"
              >
                <AtomText
                  :text="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.mySmartBattery.selectLabel'
                    )
                  "
                  font-size="text-sm"
                  font-style="font-medium"
                  class="mb-1"
                />
                <OrganismSelect
                  v-model="msbCapacitySelected"
                  :options="getAvailableMsbCapacity"
                  :is-loading="isLoadingBatteryChoices"
                  :disabled="isLoadingBatteryChoices"
                  :placeholder="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.mySmartBattery.selectPlaceholder'
                    )
                  "
                />
              </MoleculeFrameCard>
              <div class="mb-6 md:mb-0">
                <DiscountedPrice
                  :price="msbCapacitySelected?.value.monthlyPrice ?? null"
                  :discounted-price="msbDiscountedPrice"
                  data-cy="msb-price"
                />
                <br />
                <AtomText
                  is-tag="span"
                  font-size="text-xs"
                  color-variant="description"
                  :text="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.mySmartBattery.priceNote',
                      {
                        msbCapacitySelected: msbCapacitySelected?.value.capacityInkWh || '-',
                      }
                    )
                  "
                  class="mr-1"
                />
                <MoleculeStandaloneLink
                  :label="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.mySmartBattery.modifiableLink'
                    )
                  "
                  font-size="sm"
                  color-variant="brand"
                  @click="isStorageCapacityModifiableModalOpen = true"
                />
              </div>
              <MoleculeButton
                expanded
                :variant="isActive ? 'tertiary' : 'secondary'"
                :label="
                  isActive
                    ? t('subscription.solarProductionStorage.batteryStorage.common.buttonSelected')
                    : t('subscription.solarProductionStorage.batteryStorage.common.buttonSelect')
                "
                :icon-name="isActive ? 'CheckCircleIcon' : undefined"
                icon-position="left"
              />
            </template>
          </SbsSeletableCard>
          <SbsSeletableCard
            class="flex-1 md:flex-none md:grid md:grid-rows-subgrid md:row-span-6"
            :class="{ 'md:col-span-2 md:w-1/2 md:mx-auto': !isMsbCardDisplayed }"
            v-if="isMbCardDisplayed"
            v-model="selectedBattery"
            :has-error="isSubmitted && !selectedBattery"
            :value="EBatteryOptionSelectable.MB"
          >
            <template #default="{ isActive }">
              <div class="mb-6 md:mb-0">
                <AtomText
                  :text="t('subscription.solarProductionStorage.batteryStorage.myBattery.name')"
                  color-variant="description"
                  font-size="text-sm"
                />
                <AtomText
                  :text="t('subscription.solarProductionStorage.batteryStorage.myBattery.subtitle')"
                  color-variant="text"
                  font-size="text-lg"
                  fontStyle="font-semibold"
                />
                <AtomText
                  :text="
                    t('subscription.solarProductionStorage.batteryStorage.myBattery.description')
                  "
                  color-variant="text"
                  font-size="text-sm"
                />
              </div>
              <div class="mb-6 md:mb-0">
                <div class="mb-2 flex justify-between items-center">
                  <div>
                    <AtomText
                      :text="t('subscription.solarProductionStorage.batteryStorage.common.storage')"
                      color-variant="text"
                      font-size="text-sm"
                      font-style="font-medium"
                    />
                  </div>
                  <div class="text-right">
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.myBattery.storageText'
                        )
                      "
                      color-variant="text"
                      font-size="text-sm"
                    />
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.myBattery.storageDescription'
                        )
                      "
                      color-variant="description"
                      font-size="text-xs"
                    />
                  </div>
                </div>
                <div class="mb-3 flex justify-between items-center">
                  <div>
                    <AtomText
                      :text="
                        t('subscription.solarProductionStorage.batteryStorage.common.restitution')
                      "
                      color-variant="text"
                      font-size="text-sm"
                      font-style="font-medium"
                    />
                  </div>
                  <div class="text-right">
                    <AtomText
                      v-if="myBatteryRestitutionRate"
                      :text="myBatteryRestitutionRate"
                      color-variant="text"
                      font-size="text-sm"
                      data-cy="my-battery-restitution-price"
                    />
                    <AtomText
                      :text="
                        t(
                          'subscription.solarProductionStorage.batteryStorage.myBattery.restitutionDescription'
                        )
                      "
                      color-variant="description"
                      font-size="text-xs"
                    />
                  </div>
                </div>
                <OrganismAlert
                  hasIcon
                  customIconName="HeartIcon"
                  :content-text="
                    t('subscription.solarProductionStorage.batteryStorage.myBattery.alertText')
                  "
                  state="success"
                />
              </div>

              <AtomDivider class="mb-6 md:mb-0" />
              <MoleculeFrameCard
                spacing="sm"
                content-theme="dark"
                class="mt-3 mb-6 md:mt-0 md:mb-0"
              >
                <AtomText
                  :text="
                    t(
                      'subscription.solarProductionStorage.batteryStorage.myBattery.installationInfo',
                      {
                        installedPowerInkWc:
                          getCurrentSubscriptionInstallation?.installedPowerInkWc || '-',
                        monthlyPricePerkWc: formatEuroPrice(
                          batteryChoices?.myBattery?.monthlyPricePerkWc ?? null
                        ),
                      }
                    )
                  "
                  color-variant="description"
                  font-size="text-sm"
                  data-cy="my-battery-installation-info"
                />
              </MoleculeFrameCard>
              <div class="mb-6 md:mb-0">
                <MoleculeSkeleton v-if="isLoadingBatteryChoices" variant="title" />
                <DiscountedPrice
                  v-else
                  :price="batteryChoices?.myBattery?.monthlyPrice ?? null"
                  :discounted-price="mbDiscountedPrice"
                  data-cy="mb-price"
                />
                <br />
                <AtomText
                  is-tag="span"
                  font-size="text-xs"
                  color-variant="description"
                  :text="
                    t('subscription.solarProductionStorage.batteryStorage.myBattery.priceNote', {
                      installedPowerInkWc:
                        getCurrentSubscriptionInstallation?.installedPowerInkWc || '-',
                    })
                  "
                  class="mr-1"
                />
                <MoleculeStandaloneLink
                  :label="
                    t('subscription.solarProductionStorage.batteryStorage.myBattery.shippingLink')
                  "
                  target="blank"
                  to="https://intercom-help.eu/mylight150com/fr/articles/497440-comprendre-les-tarifs-de-nos-offres-d-energie#h_d9c41042f8"
                  type="url"
                  font-size="sm"
                  iconName="ExternalLinkIcon"
                  color-variant="brand"
                />
              </div>
              <MoleculeButton
                expanded
                :variant="isActive ? 'tertiary' : 'secondary'"
                :label="
                  isActive
                    ? t('subscription.solarProductionStorage.batteryStorage.common.buttonSelected')
                    : t('subscription.solarProductionStorage.batteryStorage.common.buttonSelect')
                "
                :icon-name="isActive ? 'CheckCircleIcon' : undefined"
                icon-position="left"
              />
            </template>
          </SbsSeletableCard>
        </div>

        <OrganismRadioGroupCard
          v-model="selectedBattery"
          :has-error="isSubmitted && !selectedBattery"
          :cards="[
            {
              id: '1',
              value: EBatteryOptionSelectable.NONE,
              title: t('subscription.solarProductionStorage.batteryStorage.noStorage.title'),
            },
          ]"
        />

        <AtomText
          v-if="isSubmitted && !selectedBattery"
          :text="t('subscription.solarProductionStorage.requiredError')"
          color-variant="error"
          font-size="text-sm"
          data-cy="solar-production-storage-required-error"
        />
      </form>
    </div>

    <StorageCapacityModifiableModal v-model:is-open="isStorageCapacityModifiableModalOpen" />
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, type ComputedRef, onMounted, type Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  useBatteryStore,
  useStepStore,
  useSubscriptionStore,
  useToastSnackbarStore,
} from '@/stores';

import type { BatteryOptionSelectable, MySmartBatteryChoice } from '@/types/subscription.type';
import { getIsPreloadedBatteryOfferEnabled, getIsPhysicalBatteryEnabled } from '@/utils/env.util';
import { formatEuroPrice, formatEuroPricePerkWh } from '@/utils/format.util';

import SbsSeletableCard from '@/components/snowflake/SbsSeletableCard.component.vue';
import StorageCapacityModifiableModal from '@/components/StorageCapacityModifiableModal.component.vue';
import DiscountedPrice from '@/components/subscription/DiscountedPrice.component.vue';
import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import { EBatteryOptionSelectable, EProvider } from '@/constants/subscription.constants';
import * as Sentry from '@sentry/vue';

const { addErrorToast } = useToastSnackbarStore();

const { t } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const batteryStore = useBatteryStore();

type MsbCapacitySelectOption = {
  id: number;
  value: MySmartBatteryChoice;
  label: string;
  tagValue: string | null;
  tagType: string | null;
};

const { goToNextStep, goToPreviousStep } = stepStore;

const {
  isGetSubscriptionInProgress,
  getCurrentSubscriptionId,
  getCurrentSubscriptionInstallation,
  getCurrentSubscriptionOptions,
  isEligibleForPreloadedBatteryOffer,
} = storeToRefs(subscriptionStore);

const isPreloadedBatteryOfferAlertDisplayed = computed(
  () => getIsPreloadedBatteryOfferEnabled() && isEligibleForPreloadedBatteryOffer.value
);

const { batteryChoices, isLoadingBatteryChoices, isLoadingSaveBatteryOption } =
  storeToRefs(batteryStore);
const { saveNoBattery, saveBatteryOption, findBatteryChoices } = batteryStore;

const selectedBattery = ref<BatteryOptionSelectable | null>(null);
const msbCapacitySelected: Ref<MsbCapacitySelectOption | null> = ref(null);

const isSubmitted = ref(false);
const isStorageCapacityModifiableModalOpen = ref(false);
const myBatteryRestitutionRate = computed(() => {
  const restitutionPrice = batteryChoices.value?.myBattery?.restitutionPricePerkWh;

  return restitutionPrice === null || restitutionPrice === undefined
    ? null
    : formatEuroPricePerkWh(restitutionPrice);
});

const getAvailableMsbCapacity: ComputedRef<MsbCapacitySelectOption[]> = computed(() =>
  (batteryChoices.value?.mySmartBattery ?? []).map(option => ({
    id: option.capacityInkWh,
    value: option,
    label: `${option.capacityInkWh} kWh`,
    tagValue: option.recommended ? t('subscription.solarProductionStorage.tags.recommended') : null,
    tagType: option.recommended ? 'offers' : null,
  }))
);

// A card is displayed once the back has returned its option, `null` meaning it is unavailable
const isMsbCardDisplayed = computed(() => Array.isArray(batteryChoices.value?.mySmartBattery));

const isMbCardDisplayed = computed(() => !!batteryChoices.value?.myBattery);

const isEncoreDiscountEnabled = getIsPhysicalBatteryEnabled();

const msbDiscountedPrice = computed(() =>
  isEncoreDiscountEnabled ? (msbCapacitySelected.value?.value.discountedMonthlyPrice ?? null) : null
);

const mbDiscountedPrice = computed(() =>
  isEncoreDiscountEnabled ? (batteryChoices.value?.myBattery?.discountedMonthlyPrice ?? null) : null
);

const getMsbCapacitybySize = (msbSize: number) => {
  return (
    getAvailableMsbCapacity.value?.find(capacity => capacity.value.capacityInkWh === msbSize) ??
    null
  );
};

const getMsbCapacitybySizeRecommended = () => {
  return getAvailableMsbCapacity.value?.find(capacity => capacity.value.recommended) ?? null;
};

const onSubmit = async () => {
  isSubmitted.value = true;

  if (!selectedBattery.value) {
    return;
  }

  if (
    selectedBattery.value === EBatteryOptionSelectable.MSB &&
    msbCapacitySelected.value === null
  ) {
    return;
  }

  const subscriptionId = getCurrentSubscriptionId.value;
  let success: boolean;
  if (selectedBattery.value === EBatteryOptionSelectable.NONE) {
    success = await saveNoBattery(subscriptionId);
  } else {
    success = await saveBatteryOption(subscriptionId, {
      typeBattery: selectedBattery.value,
      msbSize: msbCapacitySelected.value?.value.capacityInkWh || null,
    });
  }

  if (success) {
    await goToNextStep();
  } else {
    addErrorToast({
      id: 'failedSaveBatteryOption',
      title: t('subscription.solarProductionStorage.error.saveBatteryOption.title'),
      content: t('subscription.solarProductionStorage.error.saveBatteryOption.content'),
    });
  }
};

const selectBattery = () => {
  const battery = getCurrentSubscriptionOptions.value?.battery;
  if (battery?.requested === false) {
    selectedBattery.value = EBatteryOptionSelectable.NONE;
    return;
  }

  selectedBattery.value = battery?.option?.type ?? null;
};

const selectMsbCapacity = () => {
  const battery = getCurrentSubscriptionOptions.value?.battery;
  if (battery?.requested === false) {
    msbCapacitySelected.value = getMsbCapacitybySizeRecommended();
    return;
  }

  if (!battery?.option?.size) {
    msbCapacitySelected.value = getMsbCapacitybySizeRecommended();
    return;
  }

  msbCapacitySelected.value = getMsbCapacitybySize(battery.option.size);
};

onMounted(async () => {
  if (
    !getCurrentSubscriptionInstallation.value ||
    !getCurrentSubscriptionInstallation.value.installedPowerInkWc
  ) {
    addErrorToast({
      id: 'failedInstalledPowerInkWcNotFound',
      title: t('subscription.solarProductionStorage.error.installedPowerInkWcNotFound.title'),
      content: t('subscription.solarProductionStorage.error.installedPowerInkWcNotFound.content'),
    });

    await goToPreviousStep();
    return;
  }
  selectBattery();
  try {
    await findBatteryChoices(getCurrentSubscriptionId.value, EProvider.MYL);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'battery-choices', action: 'findBatteryChoices' },
    });
    addErrorToast({
      id: 'failedFindBatteryChoices',
      title: t('subscription.solarProductionStorage.error.findBatteryChoices.title'),
      content: t('subscription.solarProductionStorage.error.findBatteryChoices.content'),
    });
  }
  selectMsbCapacity();
});
</script>

<style scoped></style>
